import { z } from 'zod';

/**
 * Merchant health and feature usage for platform operators (ADR-0150, BR-254). Aggregates only:
 * counts and times per merchant, never sales lines, customers or other merchant documents.
 */
export const MERCHANT_HEALTH_COLOURS = ['green', 'yellow', 'red'] as const;
export type MerchantHealthColour = (typeof MERCHANT_HEALTH_COLOURS)[number];

/** Why a merchant has its colour (the first BR-254 rule that matched). */
export const MERCHANT_HEALTH_REASONS = [
  'active',
  'sales_dropping',
  'no_sale_2d',
  'never_sold',
  'inactive_7d',
] as const;
export type MerchantHealthReason = (typeof MERCHANT_HEALTH_REASONS)[number];

/** Features counted as "in use" (last 30 days, or a device that is not revoked). */
export const HEALTH_FEATURES = ['restaurant', 'onlineStore', 'devices', 'assistant'] as const;
export type HealthFeature = (typeof HEALTH_FEATURES)[number];

export const HEALTH_SORTS = ['health', 'lastSale', 'sales7d', 'name', 'registered'] as const;
export type HealthSort = (typeof HEALTH_SORTS)[number];

export const merchantHealthSchema = z.object({
  merchantId: z.string(),
  displayNameEn: z.string().nullable(),
  displayNameAr: z.string().nullable(),
  merchantStatus: z.string(),
  registeredAt: z.string(),
  daysSinceRegistration: z.number().int(),
  lastSaleAt: z.string().nullable(),
  sales7d: z.number().int(),
  salesPrev7d: z.number().int(),
  lastSignInAt: z.string().nullable(),
  activeUsers7d: z.number().int(),
  productCount: z.number().int(),
  restaurantOrders30d: z.number().int(),
  onlineOrders30d: z.number().int(),
  activeDevices: z.number().int(),
  assistantRequests30d: z.number().int(),
  features: z.array(z.enum(HEALTH_FEATURES)),
  /** Last sale, sign-in or registration, whichever is latest. */
  quietSince: z.string(),
  health: z.enum(MERCHANT_HEALTH_COLOURS),
  reason: z.enum(MERCHANT_HEALTH_REASONS),
  lastAlertAt: z.string().nullable(),
});
export type MerchantHealth = z.infer<typeof merchantHealthSchema>;

export const merchantHealthQuerySchema = z.object({
  health: z.enum(MERCHANT_HEALTH_COLOURS).optional(),
  sort: z.enum(HEALTH_SORTS).default('health'),
});
export type MerchantHealthQuery = z.input<typeof merchantHealthQuerySchema>;

export const merchantHealthListSchema = z.object({
  items: z.array(merchantHealthSchema),
  /** Counts of all merchants (before the colour filter). */
  totals: z.object({
    merchants: z.number().int(),
    green: z.number().int(),
    yellow: z.number().int(),
    red: z.number().int(),
  }),
  /** How many merchants use each feature (before the colour filter). */
  featureUsage: z.object({
    restaurant: z.number().int(),
    onlineStore: z.number().int(),
    devices: z.number().int(),
    assistant: z.number().int(),
  }),
});
export type MerchantHealthList = z.infer<typeof merchantHealthListSchema>;
